import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { UserModel } from '../../models/entities/user.model';
import { map } from 'rxjs/operators';
import { ContentfulApiService } from './contentful-api.service';
import { Entry } from 'contentful-management';
import { Utils } from '../../utils/utils';

@Injectable({
    providedIn: "root",
})
export class UserService {
    private readonly ContentTypeId = "user";
    private readonly EmailFieldId = "email";
    constructor(private contentfulApiService: ContentfulApiService) {}

    searchByEmail(email: string): Observable<UserModel | null> {
        return from(
            this.contentfulApiService.getEntries(this.ContentTypeId)
        ).pipe(
            map((items: Entry[]) => {
                const item = items!.find(
                    (item) =>
                        item.fields[this.EmailFieldId]["en-US"]?.toLowerCase() ===
                        email.toLowerCase()
                );
                if (!item) return null;
                return this.mapDtoToModel(item);
            })
        );
    }

    getAll(): Observable<UserModel[]> {
        return from(
            this.contentfulApiService.getEntries(this.ContentTypeId)
        ).pipe(
            map((items: Entry[]) => {
                const users: UserModel[] = items.map((item) => {
                    return this.mapDtoToModel(item);
                });

                return users;
            })
        );
    }

    create(data: UserModel): Promise<any> {
        const fields = {
            id: { "en-US": data.id },
            fullName: { "en-US": data.fullName },
            birthDate: { "en-US": data.birthDate },
            city: { "en-US": data.city },
            phone: { "en-US": data.phone },
            email: { "en-US": data.email },
            status: { "en-US": data.status },
            school: { "en-US": data.school },
            educationLevel: { "en-US": data.educationLevel },
            graduateYear: { "en-US": data.graduateYear },
            diplomaFileId: { "en-US": data.diplomaFileId },
            subscriptionId: { "en-US": data.subscriptionId },
        };

        return this.contentfulApiService.createEntry(
            this.ContentTypeId,
            fields
        );
    }

    update(data: UserModel): Observable<any> {
        const fields = {
            id: { "en-US": data.id },
            fullName: { "en-US": data.fullName },
            birthDate: { "en-US": data.birthDate },
            city: { "en-US": data.city },
            phone: { "en-US": data.phone },
            email: { "en-US": data.email },
            status: { "en-US": data.status },
            school: { "en-US": data.school },
            educationLevel: { "en-US": data.educationLevel },
            graduateYear: { "en-US": data.graduateYear },
            IdSubscription: { "en-US": data.subscriptionId },
        };

        return from(
            this.contentfulApiService.updateEntry(data.contentfulId, fields)
        );
    }

    mapDtoToModel(dto: any) {
        return {
            id: dto.fields["id"]["en-US"],
            fullName: dto.fields["fullName"]["en-US"],
            birthDate: dto.fields["birthDate"]["en-US"],
            city: dto.fields["city"]["en-US"],
            phone: dto.fields["phone"]["en-US"],
            email: dto.fields["email"]["en-US"],
            status: dto.fields["status"]["en-US"],
            school: dto.fields["school"]["en-US"],
            educationLevel: dto.fields["educationLevel"]["en-US"],
            graduateYear: dto.fields["graduateYear"]["en-US"],
            subscriptionId: dto.fields["subscriptionId"]["en-US"],
        } as UserModel;
    }
}
